import type { ItineraryItem, ItineraryPlan } from '@/types/itinerary';

/**
 * Pure transforms for the Smart Itinerary preview — ported from the reference
 * `TicketItineraryDialog.previewItems` and `@core/utils/itinerary-plan`. No
 * bundler/React deps so it is unit-testable in isolation. The backend is the
 * scheduling authority; these helpers only shape its rows for display.
 */

/** Resolved schedule state for an ATTRACTION preview row. */
export interface AssignedSchedule {
  /** 'ok' → booked, shows the slot window; 'none' → couldn't be placed. */
  state: 'ok' | 'none';
  /** The booked slot window ('slotStart–slotEnd'), present only for 'ok'. */
  time?: string;
}

/** A preview row — an {@link ItineraryItem} plus the derived schedule state. */
export interface PreviewRow extends ItineraryItem {
  assignedSchedule?: AssignedSchedule;
}

/** Parse an 'HH:mm' string to minutes-since-midnight, or null when malformed. */
export function toMinutes(hhmm?: string | null): number | null {
  if (typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const parts = hhmm.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/**
 * Map a generated plan's `items` straight onto ordered preview rows.
 *
 *   - ENTRY → passed through (pinned to the front).
 *   - BREAK (and any future non-attraction row) → passed through with its times.
 *   - ATTRACTION scheduled  → `assignedSchedule: { state: 'ok', time }` where
 *     `time` is the bookable SLOT window (`slotStart–slotEnd`), falling back to
 *     the execution window when an older backend omits the slot window. The
 *     execution `startTime`/`endTime` stay as the row headline.
 *   - ATTRACTION unscheduled → times nulled + `assignedSchedule: { state:'none' }`.
 *
 * The `scheduled` flag is honored when present; a legacy backend that omits it
 * falls back to trusting `startTime`/`endTime`. Rows are stable-sorted by start
 * minutes so unscheduled rows (null times) sink to the end, ENTRY stays first.
 */
export function buildPreviewRows(plan: ItineraryPlan | null | undefined): PreviewRow[] {
  const rawItems = plan?.items ?? [];
  if (rawItems.length === 0) return [];

  const minutesOf = (it: PreviewRow) => toMinutes(it.startTime ?? it.time);

  const entryItems: PreviewRow[] = [];
  const rest: PreviewRow[] = [];

  for (const item of rawItems) {
    if (!item) continue;
    if (item.type === 'ENTRY') {
      entryItems.push({ ...item });
      continue;
    }
    if (item.type !== 'ATTRACTION') {
      // BREAK (and any future non-attraction row) — pass through with its times.
      rest.push({ ...item });
      continue;
    }
    // New backend: honor `scheduled` (+ a real slotId). Legacy backend (no
    // flag): trust the times rather than render everything as unscheduled.
    const hasSchedFlag = Object.prototype.hasOwnProperty.call(item, 'scheduled');
    const ok = hasSchedFlag
      ? item.scheduled === true && item.slotId != null
      : Boolean(item.startTime && item.endTime);
    // "Jadwal dipilih" shows the booked SLOT's availability window — the actual
    // bookable time-slot — not the walked execution window. Older backends that
    // don't send slotStart/slotEnd fall back to the execution window.
    const slotWindow =
      item.slotStart && item.slotEnd
        ? `${item.slotStart}–${item.slotEnd}`
        : `${item.startTime}–${item.endTime}`;
    rest.push(
      ok
        ? { ...item, assignedSchedule: { state: 'ok', time: slotWindow } }
        : { ...item, startTime: null, endTime: null, assignedSchedule: { state: 'none' } },
    );
  }

  rest.sort((a, b) => {
    const am = minutesOf(a);
    const bm = minutesOf(b);
    if (am == null && bm == null) return 0;
    if (am == null) return 1;
    if (bm == null) return -1;
    return am - bm;
  });

  return [...entryItems, ...rest];
}

/** Inline Indonesian label + hint for an `unscheduledReason` (no i18n here). */
export interface UnscheduledReasonText {
  label: string;
  hint: string;
}

/**
 * Flattened Indonesian copy for each backend `unscheduledReason` — ported from
 * the reference `UNSCHEDULED_REASON_KEYS` + `id/translation.json`. Each reason
 * implies a different move (another date, an earlier start, fewer tickets), so
 * a bare "not scheduled" would leave the cashier with nothing to act on.
 */
const UNSCHEDULED_REASON_TEXT: Record<string, UnscheduledReasonText> = {
  NOT_PURCHASABLE: {
    label: 'Tidak dijual',
    hint: 'Wahana ini sedang tidak dijual, jadi belum bisa masuk jadwal.',
  },
  PURCHASE_CAP_REACHED: {
    label: 'Batas per sesi tercapai',
    hint: 'Satu gelang hanya boleh memegang sejumlah tiket wahana ini per sesi. Kurangi jumlahnya atau pilih tanggal lain.',
  },
  NO_SLOTS_ON_DATE: {
    label: 'Tidak ada sesi',
    hint: 'Belum ada sesi untuk wahana ini di tanggal tersebut. Coba tanggal lain.',
  },
  CAPACITY_EXHAUSTED: {
    label: 'Sesi penuh',
    hint: 'Semua sesi wahana ini sudah penuh di tanggal tersebut.',
  },
  AFTER_CLOSING: {
    label: 'Lewat jam tutup',
    hint: 'Wahana ini baru bisa dijadwalkan setelah taman tutup. Mulai lebih awal atau naikkan urutannya.',
  },
  NO_SLOT_AT_OR_AFTER_CURSOR: {
    label: 'Tidak ada sesi yang cocok',
    hint: 'Tidak ada sesi yang muat setelah wahana sebelumnya. Naikkan urutannya atau perpendek jeda jalan kaki.',
  },
};

const FALLBACK_REASON: UnscheduledReasonText = {
  label: 'Belum terjadwal',
  hint: 'Wahana ini belum bisa dimasukkan ke jadwal.',
};

/**
 * Indonesian copy for an `unscheduledReason`. An unknown reason falls back to
 * neutral copy rather than leaking the raw enum onto a cashier screen — the
 * backend may add a reason before this app ships again.
 */
export function unscheduledReasonText(reason?: string | null): UnscheduledReasonText {
  if (!reason) return FALLBACK_REASON;
  return UNSCHEDULED_REASON_TEXT[reason] ?? FALLBACK_REASON;
}
